import React, {useState} from "react";
import {Alert, Empty, Tabs, message} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {CustomerMenuHeader} from "components/layouts/customerLayout/components/customerMenuHeader";
import {CustomerFluidSubPageLayout} from "components/layouts/customerLayout/customerFluidSubPageLayout/CustomerFluidSubPageLayout";
import {
    CustomerRequestsTab,
    StyledTabs
} from "components/customer/customerRequestsPage/style/CustomerRequestsPage.styles";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TICKET_LIST_STATES, TICKET_MAX_OPEN_COUNT, TICKET_SCOPES} from "constants/ticket";
import {CreateTicketModal} from "../create/CreateTicketModal";
import {CustomerTicketList} from "./CustomerTicketList";
import {CreateTicketButton, PageHeader} from "./CustomerTickets.styles";

const states = [TICKET_LIST_STATES.open, TICKET_LIST_STATES.closed];

export const CustomerTicketsPage = observer(() => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const subCompanyId = stores.meStore.selectedSubCompanyId;
    const [state, setState] = useState(TICKET_LIST_STATES.open);
    const [createVisible, setCreateVisible] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const [, counts] = useFetch({
        fetchFn: subCompanyId
            ? stores.ticketStore.getTicketCounts.bind(null, {
                  scope: TICKET_SCOPES.customer,
                  subCompanyId
              })
            : () => ({}),
        defaultDataValue: {},
        dependencies: [subCompanyId, refreshKey]
    });
    const openCount = counts?.data?.byState?.[TICKET_LIST_STATES.open] ?? 0;

    const handleCreateClick = () => {
        if (openCount >= TICKET_MAX_OPEN_COUNT) {
            message.warning(t("customer.limitReached", {ns: "tickets"}));
            return;
        }
        setCreateVisible(true);
    };

    return (
        <CustomerFluidSubPageLayout>
            <CustomerMenuHeader />
            <CustomerRequestsTab>
                <PageHeader>
                    <h2>{t("customer.title", {ns: "tickets"})}</h2>
                </PageHeader>
                {subCompanyId ? (
                    <StyledTabs
                        activeKey={state}
                        onChange={setState}
                        destroyInactiveTabPane
                        tabBarExtraContent={
                            <CreateTicketButton
                                size="large"
                                icon={<PlusOutlined />}
                                onClick={handleCreateClick}
                            >
                                {t("myTickets.create", {ns: "tickets"})}
                            </CreateTicketButton>
                        }
                    >
                        {states.map(item => (
                            <Tabs.TabPane
                                key={item}
                                tab={`${t(`myTickets.segments.${item}`, {ns: "tickets"})} (${counts
                                    ?.data?.byState?.[item] ?? 0})`}
                            >
                                {item === TICKET_LIST_STATES.open && (
                                    <Alert
                                        showIcon
                                        type="warning"
                                        style={{marginBottom: 16}}
                                        message={t("customer.openLimitNotice", {
                                            ns: "tickets",
                                            max: TICKET_MAX_OPEN_COUNT
                                        })}
                                    />
                                )}
                                <CustomerTicketList
                                    key={`${item}-${subCompanyId}`}
                                    state={item}
                                    subCompanyId={subCompanyId}
                                    refreshKey={refreshKey}
                                    onChanged={() => setRefreshKey(previous => previous + 1)}
                                />
                            </Tabs.TabPane>
                        ))}
                    </StyledTabs>
                ) : (
                    <Empty description={t("customer.noSubCompany", {ns: "tickets"})} />
                )}
                <CreateTicketModal
                    visible={createVisible}
                    category="company"
                    ownerId={subCompanyId}
                    customerView
                    onCancel={() => setCreateVisible(false)}
                    onSuccess={() => {
                        setCreateVisible(false);
                        setState(TICKET_LIST_STATES.open);
                        setRefreshKey(previous => previous + 1);
                    }}
                />
            </CustomerRequestsTab>
        </CustomerFluidSubPageLayout>
    );
});
